import { useEffect, useState, type ReactNode } from "react";
import { ROLE_LABELS, type CampaignSummary, type CampaignsResponse, type VoiceChannel } from "@dm/shared";
import { api } from "./api.js";
import { useApp } from "./appContext.js";
import { NextSessions } from "./Schedule.js";

/** Pick the active campaign (the one the web table opens), find public ones to join, create one (if you can host), restore deleted ones. */
export function CampaignsPage() {
  const { me, reloadMe, fail } = useApp();
  const [data, setData] = useState<CampaignsResponse | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.campaigns().then(setData, fail);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const select = (id: number) =>
    run(async () => {
      await api.selectCampaign(id);
      await reloadMe();
      window.location.hash = "#/table";
    });
  const create = () =>
    run(async () => {
      if (!name.trim()) throw new Error("Give the campaign a name");
      await api.createCampaign(name.trim(), description.trim());
      setName("");
      setDescription("");
      await reloadMe();
      window.location.hash = "#/campaign";
    });

  if (!data) return <main className="muted">Loading…</main>;
  const canCreate = me.canHost || me.isAdmin;

  const card = (s: CampaignSummary, actions: ReactNode) => (
    <li key={s.campaign.id} className={`campaign-card ${s.campaign.id === data.selectedId ? "selected" : ""}`}>
      <div className="cc-main">
        <strong>{s.campaign.name}</strong>
        {s.role && <span className={`tag ${s.role === "player" ? "" : "host"}`}>{ROLE_LABELS[s.role]}</span>}
        {s.campaign.isPublic && <span className="tag">public</span>}
        {s.campaign.id === data.selectedId && <span className="tag ok-tag" title="The campaign this website opens (switch any time). Not the same as Host for tonight.">active</span>}
        <div className="muted small">
          Host: {s.campaign.ownerName ?? "—"} · {s.memberCount} member{s.memberCount === 1 ? "" : "s"} ·{" "}
          {s.campaign.rulesMode === "strict" ? "Strict 5e" : "Rule of Cool"}
          {s.campaign.voiceChannelName && ` · home #${s.campaign.voiceChannelName}`}
        </div>
        {s.liveChannelName && <div className="small"><span className="tag ok-tag">live</span> DM in <strong>#{s.liveChannelName}</strong> now</div>}
        {s.campaign.description && <p className="small cc-desc">{s.campaign.description}</p>}
      </div>
      <div className="cc-actions">{actions}</div>
    </li>
  );

  return (
    <main className="campaigns">
      <NextSessions />
      {canCreate && <SharedTables />}
      <section className="panel">
        <h3>Your campaigns</h3>
        {data.mine.length === 0 && <p className="muted">You're not in any campaign yet. Use an invite link from a Host, or request to join a public one below.</p>}
        <ul className="campaign-list">
          {data.mine.map((s) =>
            card(
              s,
              s.campaign.id === data.selectedId ? (
                <button className="primary" onClick={() => (window.location.hash = "#/table")}>
                  Open
                </button>
              ) : (
                <button className="primary" disabled={busy} onClick={() => select(s.campaign.id)}>
                  Make active
                </button>
              ),
            ),
          )}
        </ul>
      </section>

      {canCreate && (
        <section className="panel">
          <h3>Create campaign</h3>
          <div className="create-form">
            <input value={name} maxLength={80} placeholder="Campaign name" onChange={(e) => setName(e.target.value)} />
            <input value={description} maxLength={500} placeholder="Short description (optional)" onChange={(e) => setDescription(e.target.value)} />
            <button className="primary" disabled={busy || !name.trim()} onClick={create}>
              Create campaign
            </button>
          </div>
          <p className="muted small">You'll be its Host. Invite friends and link a Discord voice channel from its settings.</p>
        </section>
      )}
      {!canCreate && <p className="muted small">Want to run your own campaign? Ask an Admin to enable hosting for your account.</p>}

      <section className="panel">
        <h3>{me.isAdmin ? "Other campaigns (Admin)" : "Public campaigns"}</h3>
        {data.public.length === 0 && <p className="muted">None right now.</p>}
        <ul className="campaign-list">
          {data.public.map((s) =>
            card(
              s,
              me.isAdmin ? (
                <button disabled={busy} onClick={() => select(s.campaign.id)}>
                  Look in
                </button>
              ) : s.requestPending ? (
                <span className="muted small">Request sent: waiting for the Host or a Co-host</span>
              ) : (
                <button
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await api.requestJoin(s.campaign.id, "");
                      await load();
                    })
                  }
                >
                  Request to join
                </button>
              ),
            ),
          )}
        </ul>
      </section>

      {data.deleted.length > 0 && (
        <section className="panel">
          <h3>Recently deleted</h3>
          <ul className="campaign-list">
            {data.deleted.map((s) =>
              card(
                s,
                <>
                  <span className="muted small">
                    Deleted {new Date(s.campaign.deletedAt!).toLocaleDateString()} · restorable until{" "}
                    {new Date(Date.parse(s.campaign.deletedAt!) + 30 * 86400_000).toLocaleDateString()}
                  </span>
                  <button
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await api.restoreCampaign(s.campaign.id);
                        await load();
                        await reloadMe();
                      })
                    }
                  >
                    Restore
                  </button>
                </>,
              ),
            )}
          </ul>
        </section>
      )}
    </main>
  );
}

/**
 * Shared tables: Discord voice channels (e.g. "D&D 1", "D&D 2") where any campaign can be played.
 * /join there shows a campaign picker; the pick lasts for that sitting and doesn't change any campaign's home channel.
 */
function SharedTables() {
  const { fail } = useApp();
  const [channels, setChannels] = useState<VoiceChannel[] | null>(null);
  const load = () => api.voiceChannels().then((r) => setChannels(r.channels), fail);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!channels) return null;
  const toggle = (ch: VoiceChannel) => api.setSharedTable(ch.id, !ch.shared).then(load, fail);
  return (
    <section className="panel shared-tables">
      <h3>Shared tables</h3>
      <p className="muted small">
        Tick a Discord voice channel to make it a shared table: any Host or Co-host can /join there and pick which campaign to play.
        One campaign per channel at a time; a campaign can only be live in one channel. Campaign home channels keep working as before.
      </p>
      {channels.length === 0 ? <p className="muted small">The bot hasn't reported any voice channels yet.</p> : (
        <ul className="plain-list">
          {channels.map((ch) => (
            <li key={ch.id}>
              <label className="check-inline">
                <input type="checkbox" checked={!!ch.shared} onChange={() => void toggle(ch)} /> #{ch.name}
              </label>
              {ch.liveCampaignName && <span className="muted small"> · playing <strong>{ch.liveCampaignName}</strong> now</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
